<!-- PlanTrailActions.vue -->
<template>
  <aside
    class="action-panel"
    :class="{ collapsed: isCollapsed }"
    :aria-labelledby="headingId"
    role="complementary"
  >
    <!-- ───────────────────── Header ───────────────────── -->
    <header class="panel-header">
      <button
        class="icon-btn"
        aria-label="Collapse actions panel"
        @click="toggleCollapse"
      >
        <span class="material-icons">right_panel_close</span>
      </button>

      <h2 :id="headingId" class="panel-title">Actions</h2>
    </header>
    
    <div class="actions-main">
      <div class="panel-section d-flex">
        <div class="routing-group">
          <button class="routing-btn" @click="undoClicked">
            <span class="material-icons">undo</span>
          </button>
          <button class="routing-btn" @click="redoClicked">
            <span class="material-icons">redo</span>
          </button>
        </div>
      </div>

      <hr class="section-divider" />

    <!-- ───────────────────── Routing ───────────────────── -->
    <div class="panel-section">
      <p class="section-eyebrow">Routing</p>
      <div class="routing-group">
        <button
          v-for="mode in routingModes"
          :key="mode.value"
          class="routing-btn"
          :class="{ active: selectedMode === mode.value }"
          :title="mode.tooltip"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          data-bs-custom-class="weather-tooltip"
          @click="setRoutingMode(mode.value)"
        >
          <span class="material-icons">{{ mode.icon }}</span>
        </button>
      </div>
    </div>

    <hr class="section-divider" />

    <!-- ───────────────────── Route options ───────────────────── -->
    <div class="panel-section">
      <p class="section-eyebrow">Route options</p>
      <div class="routing-group">
        <button
          class="routing-btn"
          @click="loopRoute"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          data-bs-custom-class="weather-tooltip"
          title="Loop"
          aria-label="Loop"
        >
          <span class="material-icons">cached</span>
        </button>
        <button
          class="routing-btn"
          @click="reverseRoute"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          data-bs-custom-class="weather-tooltip"
          title="Reverse"
          aria-label="Reverse"
        >
          <span class="material-icons">sync_alt</span>
        </button>
        <button
          class="routing-btn"
          @click="backtrackRoute"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          data-bs-custom-class="weather-tooltip"
          title="Out and Back"
          aria-label="Out and back"
        >
          <span class="material-icons">keyboard_return</span>
        </button>
      </div>
    </div>

    <div class="panel-section">
      <p class="section-eyebrow">Tools</p>
      <div class="tool-list">
        <button
            class="tool-list__item"
            @click="addTracerRoute"
            data-bs-toggle="tooltip"
            data-bs-placement="left"
            data-bs-custom-class="weather-tooltip"
            title="Add a line to the map that you can trace over"
          >
          <span class="material-icons tool-list__icon">gesture</span>
          <span class="tool-list__label">Add trace</span>
        </button>
        <button
            class="tool-list__item"
            @click="showAddPointModal"
            data-bs-toggle="tooltip"
            data-bs-placement="left"
            data-bs-custom-class="weather-tooltip"
            title="Add a control point by entering coordinates"
          >
          <span class="material-icons tool-list__icon">add_location</span>
          <span class="tool-list__label">Add point by coordinate</span>
        </button>
        <button
            class="tool-list__item"
            @click="addControlPoints($event)"
            :disabled="!hasRouteCoordinates"
            data-bs-toggle="tooltip"
            data-bs-placement="left"
            data-bs-custom-class="weather-tooltip"
            title="Add evenly-spaced control points along the route"
          >
          <span class="material-icons tool-list__icon">format_line_spacing</span>
          <span class="tool-list__label">Add control points</span>
        </button>
        <button
            class="tool-list__item"
            @click="launchFlyoverForAllRoutes"
            :disabled="!hasRouteCoordinates"
            data-bs-toggle="tooltip"
            data-bs-placement="left"
            data-bs-custom-class="weather-tooltip"
            title="3D flyover preview of all routes"
          >
          <span class="material-icons tool-list__icon">flight</span>
          <span class="tool-list__label">3D Preview</span>
        </button>
        <button
            class="tool-list__item"
            @click="handleMergeAllRoutes"
            :disabled="mergeableRouteCount < 2"
            data-bs-toggle="tooltip"
            data-bs-placement="left"
            data-bs-custom-class="weather-tooltip"
            title="Merge every 'route' style route into one, and pull all routes into a single day"
          >
          <span class="material-icons tool-list__icon">merge</span>
          <span class="tool-list__label">Merge all routes</span>
        </button>

        <!-- Manage Control Points dropdown — same row style with chevron -->
        <div class="cp-menu-wrapper" ref="cpMenuRef">
          <button
            class="tool-list__item tool-list__item--toggle"
            :class="{ 'tool-list__item--active': isAreaSelecting }"
            @click="cpMenuOpen = !cpMenuOpen"
            :aria-expanded="cpMenuOpen ? 'true' : 'false'"
          >
            <span class="material-icons tool-list__icon">tune</span>
            <span class="tool-list__label">{{ isAreaSelecting ? 'Selecting area…' : 'Manage control points' }}</span>
            <span class="material-icons tool-list__chevron">{{ cpMenuOpen ? 'expand_less' : 'expand_more' }}</span>
          </button>

          <div v-show="cpMenuOpen" class="cp-dropdown">
            <button
              class="cp-dropdown__item"
              :disabled="activeRouteMiddleMarkerCount === 0"
              @click="onRemoveAllControlPoints"
            >
              <span class="material-icons cp-dropdown__icon">commit</span>
              Remove All Control Points
            </button>
            <button
              class="cp-dropdown__item"
              :class="{ 'cp-dropdown__item--active': isAreaSelecting }"
              :disabled="!isAreaSelecting && activeRouteMiddleMarkerCount === 0"
              @click="onSelectArea"
            >
              <span class="material-icons cp-dropdown__icon">highlight_alt</span>
              {{ isAreaSelecting ? 'Cancel Selection' : 'Select Area' }}
            </button>
          </div>

          <!-- Confirmation bar shown after area is drawn -->
          <div v-if="areaSelectedCount > 0" class="selection-confirm">
            <span class="selection-confirm__label">
              {{ areaSelectedCount }} point{{ areaSelectedCount === 1 ? '' : 's' }} selected
            </span>
            <div class="selection-confirm__actions">
              <button class="btn btn-danger btn-sm" @click="handleDeleteAreaSelected">Delete</button>
              <button class="btn btn-secondary btn-sm" @click="handleExitAreaSelect">Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ───────────────────── Add Point by Coordinate Modal ───────────────────── -->
    <Teleport to="body">
      <div
        ref="addPointModalRef"
        class="modal fade"
        tabindex="-1"
        aria-hidden="true"
      >
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content" style="background:#1b1b1d;color:#fff;border:1px solid rgba(255,255,255,0.1);">
            <div class="modal-header" style="border-bottom-color:rgba(255,255,255,0.08);">
              <h5 class="modal-title"><strong>Add Point by Coordinate</strong></h5>
              <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <select
                  v-model="coordFormat"
                  class="form-select rounded-pill"
                  style="background:#2a2b33;border-color:rgba(255,255,255,0.12);color:#fff;"
                >
                  <option value="latlon">Lat/Long</option>
                  <option value="ukgrid">UK Grid Reference</option>
                  <option value="irishgrid">Irish Grid Reference</option>
                </select>
              </div>
              <div class="mb-3">
                <input
                  ref="coordInputRef"
                  v-model="coordValue"
                  type="text"
                  class="form-control rounded-pill"
                  :placeholder="coordPlaceholder"
                  style="background:#2a2b33;border-color:rgba(255,255,255,0.12);color:#fff;"
                  @keydown.enter="addPointFromCoord"
                />
              </div>
              <div v-if="coordError" class="alert alert-danger py-2 mb-3">{{ coordError }}</div>
            </div>
            <div class="modal-footer" style="border-top-color:rgba(255,255,255,0.08);">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="btn btn-primary" @click="addPointFromCoord">Add</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <hr class="section-divider" />
    </div>

    <div class="save-panel">
      <div class="panel-section">
        <button class="btn btn-primary btn-block" @click="saveClicked">
          Save Plan
        </button>

        <button class="btn btn-link btn-block text-muted" @click="clearMap">
        Clear
        </button>
      </div>
    </div>
  </aside>

  <!-- ───────────────────── Expand button (collapsed) ───────────────────── -->
  <button
    v-if="isCollapsed"
    class="expand-button"
    @click="toggleCollapse"
    aria-label="Expand actions panel"
  >
    <span class="material-icons">right_panel_open</span>
    <span>Actions</span>
  </button>
</template>

<script setup lang="ts">
/* ───────────────────────── Imports ───────────────────────── */
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { Modal, Tooltip } from 'bootstrap';
import { OnBoardingGoal } from '../../../utils/models/OnBoardingGoal';
import { plainToClass } from 'class-transformer';
import { LineStringFeature } from '../../../utils/models/LineStringFeature';
import { ensureFlyoverMounted } from '../services/FlyoverMountService';
import { LngLat } from 'mapbox-gl';
import { GridRef, GridRefUtils } from '../../../utils/helpers/GridRefPreference';

/* ───────────────────────── Store / Emits ───────────────────────── */
const store = useStore();

const emit = defineEmits(['save', 'share', 'collapse']);

const cpMenuOpen = ref(false);
const cpMenuRef = ref<HTMLElement | null>(null);

/* ───────────────────────── Local state ───────────────────────── */
const headingId   = `actions-heading-${Math.random().toString(36).slice(2)}`;
const isCollapsed = ref(false);

/* ───────────────────────── Vuex-backed computed ───────────────────────── */
const routePreference = computed({
  get: () => store.state.planTrail.routePreference,
  set: value => store.commit('planTrail/setRoutePreference', value),
});
const isSnap   = computed(() => store.state.planTrail.isSnap);

/**
 *  Four modes:
 *    • 'straight' → isSnap false
 *    • 'hike'     → isSnap true &  routePreference = 'hike'
 *    • 'bike'     → isSnap true &  routePreference = 'bike'
 *    • 'car'      → isSnap true &  routePreference = 'car'
 */
const selectedMode = computed<'straight' | 'hike' | 'bike' | 'car'>(() =>
  isSnap.value ? (routePreference.value as 'hike' | 'bike' | 'car') : 'straight',
);

/* ───────────────────────── Routing button config ───────────────────────── */
const routingModes = [
  { value: 'straight', icon: 'block',            tooltip: 'Draw straight lines' },
  { value: 'hike',     icon: 'hiking',          tooltip: 'Snap to hiking paths' },
  { value: 'bike',     icon: 'directions_bike',  tooltip: 'Snap to bike routes' },
  { value: 'car',      icon: 'directions_car',   tooltip: 'Snap to roads' },
] as const;

/* ───────────────────────── 3D Preview ───────────────────────── */
const hasRouteCoordinates = computed(() => {
  const routesArray = store.getters['planTrail/routesArray'];
  return routesArray.some(route =>
    route.route_style !== 'transport' &&
    route.lineStrings &&
    route.lineStrings.length > 0
  );
});

const mergeableRouteCount = computed(() => {
  const routesArray = store.getters['planTrail/routesArray'];
  return routesArray.filter(r => !r.route_style || r.route_style === 'route').length;
});

const activeRouteMiddleMarkerCount = computed(() => {
  const activeKey = store.state.planTrail.activeRouteKey;
  if (!activeKey) return 0;
  const route = store.state.planTrail.routes[activeKey];
  if (!route) return 0;
  return Math.max(0, route.markers.length - 2);
});

async function handleMergeAllRoutes() {
  await store.dispatch('planTrail/mergeAllRoutes');
}

async function addControlPoints(event: MouseEvent) {
  // Dismiss the hover tooltip now: adding control points re-routes, which
  // briefly clears the route's lineStrings and disables this button. A disabled
  // element stops firing mouseleave, so Bootstrap would otherwise leave the
  // tooltip stuck open. Hide it up front (currentTarget is still valid before
  // the await).
  const trigger = event.currentTarget as Element | null;
  if (trigger) Tooltip.getInstance(trigger)?.hide();
  await store.dispatch('planTrail/addEvenlySpacedControlPoints');
}

const isAreaSelecting = computed(() => store.state.planTrail.isAreaSelecting);
const areaSelectedCount = computed(() => store.state.planTrail.areaSelectedMarkers.length);

function onRemoveAllControlPoints() {
  cpMenuOpen.value = false;
  handleRemoveAllControlPoints();
}

function onSelectArea() {
  cpMenuOpen.value = false;
  if (isAreaSelecting.value) {
    store.dispatch('planTrail/exitAreaSelectMode');
  } else {
    store.dispatch('planTrail/enterAreaSelectMode');
  }
}

function handleExitAreaSelect() {
  store.dispatch('planTrail/exitAreaSelectMode');
}

async function handleDeleteAreaSelected() {
  await store.dispatch('planTrail/deleteAreaSelectedMarkers');
}

function handleCpMenuClickOutside(e: MouseEvent) {
  if (cpMenuRef.value && !cpMenuRef.value.contains(e.target as Node)) {
    cpMenuOpen.value = false;
  }
}

async function handleRemoveAllControlPoints() {
  const count = activeRouteMiddleMarkerCount.value;
  if (count === 0) return;

  const confirmed = window.confirm(
    `Remove all ${count} intermediate control point${count === 1 ? '' : 's'}?`
  );
  if (!confirmed) return;

  await store.dispatch('planTrail/removeAllControlPoints');
}

async function launchFlyoverForAllRoutes() {
  if (!(window as any).user) {
    (window as any).onBoardingVue?.launchFor(OnBoardingGoal.CreateAccount);
    return;
  }

  const routesArray = store.getters['planTrail/routesArray'];
  const features = [];
  routesArray.forEach(route => {
    if (route.route_style === 'transport' || !route.lineStrings || route.lineStrings.length === 0) return;
    let allCoords = [];
    route.lineStrings.forEach(ls => {
      const lineStringFeature = plainToClass(LineStringFeature, ls);
      const coords = lineStringFeature.lineStringGeometry.coordinates;
      if (coords && coords.length > 0) {
        if (allCoords.length > 0) {
          allCoords = allCoords.concat(coords.slice(1));
        } else {
          allCoords = allCoords.concat(coords);
        }
      }
    });
    if (allCoords.length >= 2) {
      features.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates: allCoords },
        properties: { name: route.name, routeStyle: route.route_style }
      });
    }
  });
  const fc = { type: "FeatureCollection", features };
  if (fc.features.length === 0) return;

  const ready = await ensureFlyoverMounted();
  if (ready) {
    (window as any).showMapModalForCoordinates(fc);
  }
}

/* ───────────────────────── Methods ───────────────────────── */
function setRoutingMode(mode: 'straight' | 'hike' | 'bike' | 'car') {
  if (mode === 'straight') {
    if (isSnap.value) store.commit('planTrail/toggleSnap');
    return;
  }
  if (!isSnap.value) store.commit('planTrail/toggleSnap');
  if (routePreference.value !== mode) store.commit('planTrail/setRoutePreference', mode);
}

const undoClicked     = () => store.dispatch('planTrail/undo');
const redoClicked     = () => store.dispatch('planTrail/redo');
const loopRoute       = () => store.dispatch('planTrail/loopRoute');
const reverseRoute    = () => store.dispatch('planTrail/reverseFullRoute');
const backtrackRoute  = () => store.dispatch('planTrail/backtrackRoute');
// const updateElevation = () => store.dispatch('planTrail/updateElevation');


function addTracerRoute() {
  const input = document.getElementById('file-selector') as HTMLInputElement | null;
  if (!input) return;

  input.type = 'file';
  input.accept = '.gpx,.kml,.geojson,application/gpx+xml,application/vnd.google-earth.kml+xml,application/json';
  input.value = '';

  input.onchange = async (e: Event) => {
    const target = e.target as HTMLInputElement | null;
    if (!target || !target.files || target.files.length === 0) return;
    const file = target.files[0];

    try {
      await store.dispatch('planTrail/addTracerRoute', file);
    } finally {
      target.value = '';
    }
  };

  input.click();
}

/* ── Save / share / clear ───────────────────────── */
async function saveClicked() {
  if (window.user == null) {
    window.onBoardingVue?.launchFor(OnBoardingGoal.CreateAccount);
    return;
  }

  // Check for and remove empty routes (routes with less than 2 markers)
  const routes = store.state.planTrail.routes;
  const emptyRouteKeys: string[] = [];

  Object.keys(routes).forEach(routeKey => {
    const route = routes[routeKey];
    if (route.markers.length < 2) {
      emptyRouteKeys.push(routeKey);
      console.log(`[SAVE] Removing empty route: "${route.name}" (Day ${route.day ?? 'N/A'}, Style: ${route.route_style ?? 'route'}, Markers: ${route.markers.length})`);
    }
  });

  if (emptyRouteKeys.length > 0) {
    console.log(`[SAVE] Found ${emptyRouteKeys.length} empty route(s), removing automatically...`);

    // Remove all empty routes
    for (const routeKey of emptyRouteKeys) {
      store.commit('planTrail/removeRoute', routeKey);
    }

    // Refresh the map display
    await store.dispatch('planTrail/addAllRoutesToMap');

    console.log(`[SAVE] Successfully removed ${emptyRouteKeys.length} empty route(s)`);
  } else {
    console.log('[SAVE] No empty routes found');
  }

  if (store.getters['planTrail/isTransportOnlyPlan']) {
    alert('Transport-only plans cannot be saved. Please add a hiking segment before saving your plan.');
    return;
  }

  // Validate that the plan has at least one main route (route type)
  if (!store.getters['planTrail/hasMainRoute']) {
    alert('Your plan must contain at least one route type segment. Please add a route segment before saving.');
    return;
  }

  // Validate that each hike day has at least one main route
  const dayValidation = store.getters['planTrail/validateHikeDaysHaveMainRoute'];
  if (!dayValidation.valid) {
    const dayList = dayValidation.invalidDays.map((d: number) => `Day ${d}`).join(', ');
    alert(`Each hike day must contain at least one route or transport segment. The following days only have alt or spur segments: ${dayList}`);
    return;
  }

  const element = document.getElementById('save-plan-dialog');
  const modal   = new Modal(element!);
  modal.show();
  emit('save');
}

function shareClicked() {
  if (window.user == null) {
    window.onBoardingVue?.launchFor(OnBoardingGoal.CreateAccount);
    return;
  }
  store.dispatch('planTrail/shareRoute');
  emit('share');
}

const clearMap = () => store.dispatch('planTrail/clearMap');

/* ── Add point by coordinate ───────────────────────── */
const addPointModalRef = ref<HTMLElement | null>(null);
const coordInputRef = ref<HTMLInputElement | null>(null);
const coordFormat = ref('latlon');
const coordValue = ref('');
const coordError = ref('');

const coordPlaceholder = computed(() => {
  switch (coordFormat.value) {
    case 'ukgrid': return 'Enter UK Grid Ref (e.g. SU 73520 96536)';
    case 'irishgrid': return 'Enter Irish Grid Ref (e.g. H 123456 654321)';
    default: return 'Enter Lat/Long (e.g. 53.123, -7.123)';
  }
});

function showAddPointModal() {
  coordValue.value = '';
  coordError.value = '';
  if (addPointModalRef.value) {
    const modal = Modal.getOrCreateInstance(addPointModalRef.value);
    modal.show();
    nextTick(() => coordInputRef.value?.focus());
  }
}

async function parseCoordinate(): Promise<LngLat> {
  // Normalize: collapse whitespace to single spaces, trim
  const input = coordValue.value.replace(/\s+/g, ' ').trim();
  if (!input) throw new Error('Please enter coordinates.');

  switch (coordFormat.value) {
    case 'latlon': {
      const cleaned = input.replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
      const parts = cleaned.split(' ');
      if (parts.length >= 2) {
        const lat = parseFloat(parts[0]);
        const lon = parseFloat(parts[1]);
        if (!isNaN(lat) && !isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
          return new LngLat(lon, lat);
        }
      }
      throw new Error('Invalid Lat/Long format. Expected: latitude longitude (e.g. 53.123 -7.123)');
    }
    case 'ukgrid': {
      return await convertGridRefToLngLat(input, 'ukgrid');
    }
    case 'irishgrid': {
      return await convertGridRefToLngLat(input, 'irishgrid');
    }
    default:
      throw new Error('Unknown coordinate format selected');
  }
}

async function convertGridRefToLngLat(input: string, type: 'ukgrid' | 'irishgrid'): Promise<LngLat> {
  console.log('[AddPoint] convertGridRefToLngLat called:', type, input);
  try {
    let result: LngLat;
    if (type === 'ukgrid') {
      const utils = new GridRefUtils(GridRef.GbGrid);
      result = await utils.convertLetteredGBGridRefToLatLng(input);
    } else {
      const utils = new GridRefUtils(GridRef.IeGrid);
      result = await utils.convertLetteredIrishGridRefToLonLat(input);
    }
    console.log('[AddPoint] Conversion result:', result);
    // The GridRefUtils methods return a LngLat, but validate it
    if (!result || isNaN(result.lng) || isNaN(result.lat)) {
      throw new Error('Conversion returned invalid coordinates.');
    }
    return result;
  } catch (error: any) {
    console.error('[AddPoint] Conversion error:', error);
    const label = type === 'ukgrid' ? 'UK Grid Reference' : 'Irish Grid Reference';
    throw new Error(error?.message || `Could not convert ${label}. Please check the format.`);
  }
}

async function addPointFromCoord() {
  coordError.value = '';
  try {
    const lngLat = await parseCoordinate();
    await store.dispatch('planTrail/addMarkerAtCoordinates', { lng: lngLat.lng, lat: lngLat.lat });

    // Fly to the added point
    if ((window as any).map) {
      (window as any).map.flyTo({ center: [lngLat.lng, lngLat.lat], zoom: 15, essential: true });
    }

    // Close modal
    if (addPointModalRef.value) {
      Modal.getInstance(addPointModalRef.value)?.hide();
    }
  } catch (error: any) {
    coordError.value = error?.message || 'Invalid coordinate format. Please check your input.';
  }
}

/* ── Panel collapse ───────────────────────── */
function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value;
  emit('collapse');
}

/* ── Tooltips + click-outside ───────────────────────── */
onMounted(() => {
  const triggers = document.querySelectorAll('[data-bs-toggle="tooltip"]');
  [...triggers].forEach(el => new Tooltip(el));
  document.addEventListener('click', handleCpMenuClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleCpMenuClickOutside);
});
</script>

<style scoped>
/* ───────────────────────── Panel container ───────────────────────── */
.action-panel {
  position: absolute;
  top: 1rem;
  right: 1rem;
  height: 85vh;
  width: 280px;
  background: rgb(27 27 29 / 90%);
  color: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  overflow: hidden; /* inner regions handle their own scrolling */
  z-index: 500;
  transition: transform 0.3s ease, opacity 0.3s ease;
}
/* Collapsed state */
.action-panel.collapsed {
  transform: translateX(100%);
  opacity: 0;
  pointer-events: none;
}

/* ── Header ── */
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.panel-title { margin: 0; font-size: 1.1rem; font-weight: 600; }
.icon-btn    { background: transparent; border: none; color: inherit; cursor: pointer; padding: .25rem; line-height: 0; }
.icon-btn:hover { color: #ffd600; }

/* ── Sections ── */
.panel-section,
.panel-section-header { padding: 0.5rem 0.85rem; }
.panel-section-header { padding-bottom: 0; }
.section-divider {
  margin: 0.15rem 0;
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.2)
}

.section-eyebrow {
  margin: 0 0 0.75rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1;
}

.actions-main{
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0; /* allow this flex child to shrink so it can scroll */
}

.save-panel {
  flex: 0 0 auto;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: rgb(27 27 29 / 95%);
}

/* ── Grid buttons ── */
.button-grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-column-gap: .75em;
  grid-row-gap: .75em;
}

/* ── Square icon button ── */
.square { width: 40px; height: 38px; padding: 0; }

/* ── Expand button when collapsed ── */
.expand-button {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: var(--dark);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
  cursor: pointer;
  z-index: 499;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  transition: background 0.2s;
}
.expand-button:hover { background: #2c2c2e; }

/* ── Scrollbar (now lives on the inner actions region) ── */
.actions-main::-webkit-scrollbar { width: 6px; }
.actions-main::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.15); border-radius: 3px; }
.actions-main::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.25); }

/* ── Responsive ── */
@media (max-width: 768px) {
  .action-panel { right: 0.5rem; width: 280px; }
}

/* ───────────────────────── New circular routing buttons ───────────────────────── */
.routing-group { display: flex; gap: 0.5rem; }
.routing-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: var(--dark);
  color: #ffffff;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
}
.routing-btn .material-icons { font-size: 20px; }
.routing-btn:hover  { background: #3c3c3c; }
.routing-btn.active {
  background: #ffd600;  /* yellow when active */
  color: #202020;       /* dark icon */
  font-weight: 700;
}

.button-grid.panel-section {
  display: grid !important;         /* force grid, beats stray flex overrides */
  grid-template-columns: 1fr 1fr;   /* two equal columns                     */
}

/* ── Tool list (Tools section) ────────────────────────────── */
.tool-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tool-list__item {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.5rem 0.55rem;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.2;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.tool-list__item:hover {
  background: rgba(255, 255, 255, 0.08);
}

.tool-list__item:hover .tool-list__icon {
  color: #ffd600;
}

.tool-list__item:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.tool-list__item:disabled:hover {
  background: transparent;
}

.tool-list__item:disabled:hover .tool-list__icon {
  color: rgba(255, 255, 255, 0.7);
}

.tool-list__icon {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.75);
  flex-shrink: 0;
  transition: color 0.15s;
}

.tool-list__label {
  flex: 1 1 auto;
  min-width: 0;
}

.tool-list__chevron {
  font-size: 18px;
  opacity: 0.6;
  flex-shrink: 0;
  margin-left: auto;
}

.tool-list__item--toggle[aria-expanded="true"] .tool-list__chevron {
  opacity: 1;
}

.tool-list__item--active {
  background: rgba(253, 223, 0, 0.1);
  color: #fddf00;
}

.tool-list__item--active .tool-list__icon { color: #fddf00; }
.tool-list__item--active:hover { background: rgba(253, 223, 0, 0.18); }

.cp-menu-wrapper {
  position: relative;
}

.cp-dropdown {
  margin-top: 0.35rem;
  background: #2f303b;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  overflow: hidden;
}

.cp-dropdown__item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 0.75rem;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 0.82rem;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;
}

.cp-dropdown__item:hover {
  background: rgba(255, 255, 255, 0.08);
}

.cp-dropdown__item:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.cp-dropdown__item--active {
  color: #fddf00;
}

.cp-dropdown__icon {
  font-size: 18px;
  flex-shrink: 0;
}

.selection-confirm {
  margin-top: 0.75rem;
  padding: 0.6rem 0.75rem;
  background: rgba(253, 223, 0, 0.1);
  border: 1px solid rgba(253, 223, 0, 0.4);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.selection-confirm__label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #fddf00;
}

.selection-confirm__actions {
  display: flex;
  gap: 0.5rem;
}

.selection-confirm__actions .btn {
  flex: 1;
  font-size: 0.75rem;
  padding: 0.3rem 0.5rem;
}
</style>
